import { Component, DestroyRef, OnInit, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { debounceTime } from 'rxjs';
import { DraggableContainerComponent, DraggableItem, QaiButtonComponent, QaiIconComponent } from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { KnowledgeCheckAnswerPointRowComponent } from './knowledge-check-answer-point-row/knowledge-check-answer-point-row.component';
import { IKnowledgeCheckQuestionForm, KnowledgeCheckAnswerPointVerb } from 'src/app/interfaces/self-serve/isimulation-details-information';
import { IGuidedAnswerPointUpdate } from 'src/app/interfaces/self-serve/requests/iguided-questions-update';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';

// Container for the answer points section inside a KC question accordion.
// createGuidedAnswer is called immediately on "Add new answer point" and retried
// on subsequent value changes if the initial call failed (row emits createRetryRequested).
@Component({
  selector: 'knowledge-check-answer-points',
  standalone: true,
  templateUrl: './knowledge-check-answer-points.component.html',
  styleUrls: ['./knowledge-check-answer-points.component.scss'],
  imports: [
    ReactiveFormsModule,
    DraggableContainerComponent,
    KnowledgeCheckAnswerPointRowComponent,
    QaiButtonComponent,
    QaiIconComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class KnowledgeCheckAnswerPointsComponent implements OnInit {
  questionForm = input.required<FormGroup>();
  // Drop list id and the other questions' list ids, so answer points can move between questions.
  listId = input<string>();
  connectedTo = input<string[]>([]);
  // The answer points before a drop; the question list saves the new order and handles a move in.
  answerPointsReordered = output<FormGroup[]>();

  callUuid: string = '';
  readOnly: boolean = false;
  answerPointItems: DraggableItem[] = [];
  expandedCustomFeedback = new Set<FormGroup>();

  private pendingCreates = new Set<FormGroup>();
  // Answer points whose delete request is in flight; their delete button stays disabled until it returns.
  private deletingAnswerPoints = new Set<FormGroup>();
  private destroyRef = inject(DestroyRef);

  constructor(
    private route: ActivatedRoute,
    private authorService: AuthorService,
    private authorReactiveFormService: AuthorReactiveFormService,
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap?.get('uuid')!;
    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);
    this.syncItems();
    const persistedUuids = SelfServeLocalStorageUtil.getExpandedCustomFeedbackUuids();
    this.answerPoints.controls.forEach((ctrl) => {
      const group = ctrl as FormGroup;
      const uuid: string = group.get('uuid')?.value ?? '';
      if (group.get('displayFactIncorrect')?.value || persistedUuids.includes(uuid)) {
        this.expandedCustomFeedback.add(group);
      }
    });

    this.answerPoints.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.saveAnswerPoints());
  }

  get answerPoints(): FormArray<FormGroup> {
    return this.questionForm().get('answerPoints') as FormArray<FormGroup>;
  }

  get questionSaved(): boolean {
    return !!this.questionForm().get('questionUuid')?.value;
  }

  addAnswerPoint(): void {
    const maxSequence = this.answerPoints.controls.reduce(
      (max, ctrl) => Math.max(max, (ctrl as FormGroup).get('sequence')?.value ?? 0), 0,
    );
    const nextSequence = maxSequence + 1;
    const newGroup = this.authorReactiveFormService.buildAnswerPointGroup({ sequence: nextSequence });
    this.answerPoints.push(newGroup, { emitEvent: false });
    this.syncItems();
    this.performCreate(newGroup);
  }

  handleCreateRetry(group: FormGroup): void {
    this.performCreate(group);
  }

  handleDeleteRequested(event: { index: number; uuid: string }): void {
    const { index, uuid } = event;

    if (!uuid) {
      this.answerPoints.removeAt(index);
      this.syncItems();
      return;
    }

    const group = this.answerPoints.at(index);
    if (this.deletingAnswerPoints.has(group)) return;
    this.deletingAnswerPoints.add(group);

    const callGuidedUuid = this.questionForm().get('callGuidedUuid')?.value ?? '';
    this.authorService.deleteAnswerPoint(this.callUuid, callGuidedUuid, uuid).subscribe({
      next: () => {
        this.deletingAnswerPoints.delete(group);
        this.answerPoints.removeAt(index);
        this.syncItems();
      },
      error: (err) => {
        this.deletingAnswerPoints.delete(group);
        console.error('Failed to delete answer point:', err);
      },
    });
  }

  isDeleting(group: FormGroup): boolean {
    return this.deletingAnswerPoints.has(group);
  }

  onItemsReordered(items: DraggableItem[]): void {
    const reordered = items as unknown as FormGroup[];
    const previous = this.answerPoints.controls.slice();
    while (this.answerPoints.length > 0) {
      this.answerPoints.removeAt(0, { emitEvent: false });
    }
    reordered.forEach((ctrl) => this.answerPoints.push(ctrl, { emitEvent: false }));
    this.syncItems();
    this.answerPointsReordered.emit(previous);
  }

  isCustomFeedbackExpanded(group: FormGroup): boolean {
    return this.expandedCustomFeedback.has(group);
  }

  onCustomFeedbackToggled(group: FormGroup): void {
    this.expandedCustomFeedback.add(group);
    const uuid: string = group.get('uuid')?.value ?? '';
    SelfServeLocalStorageUtil.setExpandedCustomFeedbackUuid(uuid);
  }

  asFormGroup(item: DraggableItem): FormGroup {
    return item as unknown as FormGroup;
  }

  indexOf(item: DraggableItem): number {
    return this.answerPoints.controls.indexOf(item as unknown as FormGroup);
  }

  private performCreate(group: FormGroup): void {
    if (this.pendingCreates.has(group)) return;
    this.pendingCreates.add(group);

    const callGuidedUuid = this.questionForm().get('callGuidedUuid')?.value ?? '';
    const questionUuid = this.questionForm().get('questionUuid')?.value ?? '';
    const answerSequence = group.get('sequence')?.value ?? 1;

    this.authorService.createGuidedAnswer(this.callUuid, {
      callGuidedUuid,
      questionUuid,
      answerSequence,
      verb: KnowledgeCheckAnswerPointVerb.State.toUpperCase(),
      displayFactCorrect: '',
      displayFactIncorrect: null,
      factText: null,
    }).subscribe({
      next: (res) => {
        group.patchValue({ uuid: res.uuid }, { emitEvent: false });
        this.pendingCreates.delete(group);
        if (this.expandedCustomFeedback.has(group)) {
          SelfServeLocalStorageUtil.setExpandedCustomFeedbackUuid(res.uuid);
        }
      },
      error: (err) => {
        console.error('Failed to create answer point:', err);
        this.pendingCreates.delete(group);
      },
    });
  }

  private saveAnswerPoints(): void {
    if (this.readOnly) return;
    const qv = this.questionForm().getRawValue() as IKnowledgeCheckQuestionForm;
    if (!qv.questionUuid) return;
    if (this.answerPoints.controls.some((c) => !c.get('uuid')?.value)) return;

    const answers: IGuidedAnswerPointUpdate[] = this.answerPoints.controls.map((ctrl) => {
      const g = ctrl as FormGroup;
      return {
        uuid: g.get('uuid')?.value ?? '',
        questionUuid: qv.questionUuid,
        answerSequence: g.get('sequence')?.value ?? 0,
        verb: (g.get('verb')?.value ?? '').toUpperCase(),
        displayFactCorrect: g.get('displayText')?.value ?? '',
        displayFactIncorrect: (g.get('displayFactIncorrect')?.value ?? '').trim() || null,
        factText: null,
      };
    });

    this.authorService.updateGuidedQuestionsWithAnswers(this.callUuid, {
      questions: [{
        callGuidedUuid: qv.callGuidedUuid,
        questionUuid: qv.questionUuid,
        questionName: qv.title ?? '',
        questionScript: qv.script,
        questionSequence: qv.sequence ?? null,
        hints: qv.hint,
        reference: qv.resourceNote ?? '',
        answers,
      }],
    }).subscribe({
      error: (err) => console.error('Error saving answer points:', err),
    });
  }

  private syncItems(): void {
    this.answerPointItems = this.answerPoints.controls.slice() as unknown as DraggableItem[];
  }
}
